import { Link } from "@tanstack/react-router";
import { Fragment } from "react";

export type Crumb = {
  name: string;
  // Route path. Use with params for dynamic routes. Omit on the current page.
  to?: string;
  params?: Record<string, string>;
};

/**
 * Site-wide breadcrumb trail. Renders semantic <nav> with ordered links to
 * every ancestor plus the current page label. Uses the same "eyebrow" styling
 * previously used on the airports/FBOs detail pages so it looks native on
 * every route it's added to.
 *
 * JSON-LD BreadcrumbList is already emitted from each route's head() via
 * breadcrumbJsonLd(), so this component intentionally renders visual markup
 * only — no duplicate structured data.
 */
export function Breadcrumbs({ items, className = "" }: { items: Crumb[]; className?: string }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className={`eyebrow ${className}`}>
      <ol className="flex flex-wrap items-center">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <Fragment key={`${c.name}-${i}`}>
              <li>
                {c.to && !isLast ? (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  <Link to={c.to as any} params={c.params as any} className="hover:text-gold-soft">
                    {c.name}
                    <span className="sr-only"> (breadcrumb)</span>
                  </Link>
                ) : (
                  <span aria-current={isLast ? "page" : undefined} className={isLast ? "text-foreground" : ""}>
                    {c.name}
                  </span>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true" className="mx-2">
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
